import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { usePermissions } from '../context/PermissionsContext';
import { prefetchRoute } from '../lib/routePrefetch';
import axios from 'axios';
import { toast } from 'sonner';
import { Reorder } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import NewArticleNotification from '../components/NewArticleNotification';
import NotificationBell from '../components/NotificationBell';
import ArticleModal from '../components/ArticleModal';
import ReminderNotifications from '../components/ReminderNotifications';
import ObjectivesReminder from '../components/ObjectivesReminder';
import ClarificationAlert from '../components/ClarificationAlert';
import NotePopupGate from '../components/NotePopupGate';
import { useOctopusQuizGate } from '../components/quiz/QuizGate';
import { ImpersonationSelector } from '../components/ImpersonationSelector';
import { 
  LayoutDashboard, 
  Map as MapIcon, 
  FileText,
  ClipboardList,
  Target,
  BarChart3,
  Users, 
  LogOut, 
  User,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Menu,
  X,
  Wrench,
  Info,
  Car,
  Fuel,
  RotateCw,
  Database,
  Sun,
  AlertTriangle,
  Moon,
  GraduationCap,
  MessageSquare,
  Bell,
  Phone,
  Eye,
  EyeOff,
  KeyRound,
  FolderOpen,
  Bot,
  GripVertical,
  ArrowUpDown,
  Check,
  Loader2,
} from 'lucide-react';
import { useState, useEffect } from 'react';
import { ChevronDown, ChevronRight as ChevronRightSmall, Lock, Network, Building2 } from 'lucide-react';
import { useOrgAccess } from '@/hooks/useOrgAccess';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Layout() {
  const { user, logout, isImpersonating, impersonation, stopImpersonation } = useAuth();
  const { canAccess } = usePermissions();
  const orgAccess = useOrgAccess();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const quizGate = useOctopusQuizGate(!!user && user.role !== 'admin');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [hasVoiture, setHasVoiture] = useState(false);
  const [loadingVoiture, setLoadingVoiture] = useState(true);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [showArticleModal, setShowArticleModal] = useState(false);
  const [notificationKey, setNotificationKey] = useState(0);
  const [trainingVisibleToAll, setTrainingVisibleToAll] = useState(false);
  const [showImpersonationModal, setShowImpersonationModal] = useState(false);
  
  // Changement de mot de passe
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ old_password: '', new_password: '', confirm: '' });
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState(false);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordError('');
    if (passwordForm.new_password !== passwordForm.confirm) {
      setPasswordError('Les nouveaux mots de passe ne correspondent pas.');
      return;
    }
    if (passwordForm.new_password.length < 6) {
      setPasswordError('Le mot de passe doit contenir au moins 6 caractères.');
      return;
    }
    setPasswordLoading(true);
    try {
      await axios.put(`${API_URL}/users/me/password`, {
        old_password: passwordForm.old_password,
        new_password: passwordForm.new_password,
      });
      setPasswordSuccess(true);
      setPasswordForm({ old_password: '', new_password: '', confirm: '' });
      setTimeout(() => {
        setShowPasswordModal(false);
        setPasswordSuccess(false);
      }, 2000);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Erreur lors du changement de mot de passe.';
      setPasswordError(detail === 'Incorrect old password' ? 'Mot de passe actuel incorrect.' : detail);
    } finally {
      setPasswordLoading(false);
    }
  };
  
  // Chat visibility state
  const [chatEnabled, setChatEnabled] = useState(false);
  const [canManageChat, setCanManageChat] = useState(false);
  const [loadingChatSettings, setLoadingChatSettings] = useState(true);
  const [chatUnreadCount, setChatUnreadCount] = useState(0);
  const [userHasChatAccess, setUserHasChatAccess] = useState(false);

  // Scroller vers le haut à chaque changement d'onglet
  useEffect(() => {
    // Scroll both window and the main container
    window.scrollTo(0, 0);
    const main = document.querySelector('main');
    if (main) main.scrollTo(0, 0);
  }, [location.pathname]);

  // Vérifier si l'utilisateur a une voiture attribuée
  useEffect(() => {
    const checkUserVoiture = async () => {
      if (user?.role === 'vendor' || user?.role === 'manager') {
        try {
          const response = await axios.get(`${API_URL}/voitures`);
          setHasVoiture(response.data.length > 0);
        } catch (error) {
          console.error('Error checking voiture:', error);
          setHasVoiture(false);
        } finally {
          setLoadingVoiture(false);
        }
      } else {
        setLoadingVoiture(false);
      }
    };

    if (user) {
      checkUserVoiture();
    }
  }, [user]);

  // Auto-show popup on login if there are unread notifications
  useEffect(() => {
    const checkAndShowNotifications = async () => {
      if (!user) return;
      
      try {
        const response = await axios.get(`${API_URL}/articles/notifications`);
        
        const notifications = response.data.notifications || [];
        if (notifications.length > 0) {
          // Show the first notification automatically
          setSelectedArticle(notifications[0]);
          setShowArticleModal(true);
        }
      } catch (error) {
        console.error('Error fetching notifications for auto-popup:', error);
      }
    };

    checkAndShowNotifications();
  }, [user]);

  // Fetch app settings for training visibility
  useEffect(() => {
    const fetchSettings = async () => {
      if (!user) return;
      
      try {
        const response = await axios.get(`${API_URL}/settings`);
        setTrainingVisibleToAll(response.data.training_visible_to_all || false);
      } catch (error) {
        console.error('Error fetching settings:', error);
      }
    };

    fetchSettings();
  }, [user]);

  // Fetch chat visibility settings
  useEffect(() => {
    const fetchChatSettings = async () => {
      if (!user) return;
      
      try {
        const visibilityRes = await axios.get(`${API_URL}/chat/settings/visibility`);
        setChatEnabled(visibilityRes.data.enabled || false);
        
        const canManageRes = await axios.get(`${API_URL}/chat/settings/can-manage`);
        setCanManageChat(canManageRes.data.can_manage || false);

        // Vérifier si l'utilisateur (manager/vendor) a accès via membership
        const accessRes = await axios.get(`${API_URL}/chat/has-access`);
        setUserHasChatAccess(accessRes.data.has_access || false);
      } catch (error) {
        console.error('Error fetching chat settings:', error);
      } finally {
        setLoadingChatSettings(false);
      }
    };

    fetchChatSettings();
  }, [user]);

  // Fetch chat unread count
  const fetchChatUnreadCount = async () => {
    if (!user) return;
    if (!chatEnabled && !canManageChat && !userHasChatAccess) return;
    
    try {
      const response = await axios.get(`${API_URL}/chat/channels`);
      const channels = response.data.channels || [];
      const totalUnread = channels.reduce((sum, ch) => sum + (ch.unread_count || 0), 0);
      setChatUnreadCount(totalUnread);
    } catch (error) {
      // Silent fail for chat notifications
    }
  };

  // Poll for chat unread count
  useEffect(() => {
    if (!user) return;
    if (!chatEnabled && !canManageChat && !userHasChatAccess) return;
    
    fetchChatUnreadCount();
    const interval = setInterval(fetchChatUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [user, chatEnabled, canManageChat, userHasChatAccess]);

  // Toggle chat visibility
  const toggleChatVisibility = async () => {
    try {
      const newValue = !chatEnabled;
      
      await axios.put(`${API_URL}/chat/settings/visibility?enabled=${newValue}`, {});
      
      setChatEnabled(newValue);
    } catch (error) {
      console.error('Error toggling chat visibility:', error);
    }
  };

  const handleArticleClick = (article) => {
    setSelectedArticle(article);
    setShowArticleModal(true);
  };

  const handleArticleClose = () => {
    setShowArticleModal(false);
    setSelectedArticle(null);
  };

  const handleArticleRead = () => {
    // Refresh notifications
    setNotificationKey(prev => prev + 1);
  };

  const handleArticleRemindLater = () => {
    // Refresh notifications
    setNotificationKey(prev => prev + 1);
  };

  const handleRefresh = () => {
    // Reload the page to refresh all data
    window.location.reload();
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const [collapsedGroups, setCollapsedGroups] = useState(() => {
    try { return JSON.parse(localStorage.getItem('sidebar_groups') || '{}'); }
    catch { return {}; }
  });

  const toggleGroup = (id) => {
    setCollapsedGroups(prev => {
      const next = { ...prev, [id]: !prev[id] };
      localStorage.setItem('sidebar_groups', JSON.stringify(next));
      return next;
    });
  };

  // ── Groupe Octopus Energy ───────────────────────────────────────────────────
  // RÈGLE #1 : items Octopus visibles UNIQUEMENT si l'user a accès au partenaire 'octopus'.
  // allowed_partners est toujours rempli par /auth/me et /auth/impersonate.
  const hasOctopus =
    (user?.allowed_partners || []).some(p => p.slug === 'octopus');
  const octopusItems = [];
  if (hasOctopus) {
    octopusItems.push({ to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' });
    if (canAccess('quality-overview')) {
      octopusItems.push({ to: '/quality-overview', icon: Phone, label: 'Overview Qualité' });
    }
    if (canAccess('contracts')) {
      octopusItems.push({ to: '/contracts', icon: FileText, label: 'Contrats' });
    }
    if (canAccess('suivi-client')) {
      octopusItems.push({ to: '/suivi-client', icon: ClipboardList, label: 'Suivi client' });
    }
    if (canAccess('secteurs')) {
      octopusItems.push({ to: '/secteurs', icon: MapIcon, label: 'Secteur et mairie' });
    }
  }

  // ── Groupe RT Distribution ──────────────────────────────────────────────────
  const rtItems = [];
  // Infos accessible à tous les partenaires (transverse) — pas de gate octopus
  if (canAccess('infos')) {
    rtItems.push({ to: '/infos', icon: Info, label: 'Infos' });
  }
  if (canAccess('outils')) {
    rtItems.push({ to: '/outils', icon: Wrench, label: 'Outils et Demandes' });
  }
  if (canAccess('formation') || trainingVisibleToAll) {
    rtItems.push({ to: '/formation', icon: GraduationCap, label: 'Formation' });
  }
  if ((canAccess('chat') && (chatEnabled || canManageChat)) || userHasChatAccess) {
    rtItems.push({ to: '/chat', icon: MessageSquare, label: 'Chat' });
  }
  if (canAccess('voitures')) {
    if (user?.role === 'admin') {
      rtItems.push({ to: '/voitures', icon: Car, label: 'Voitures' });
    } else if ((user?.role === 'manager' || user?.role === 'vendor') && hasVoiture) {
      rtItems.push({ to: '/voitures', icon: Car, label: 'Voiture' });
    }
  }
  // ── Carte agences (admin + responsable d'agence + profils rattachés à l'agence "Maison mère") ──
  const isMaisonMere = (user?.agence_ville || '').trim().toLowerCase() === 'maison mère';
  if (((user?.role === 'admin' || user?.role === 'responsable_agence') && canAccess('agence-map')) || (isMaisonMere && (user?.role === 'manager' || user?.role === 'vendor'))) {
    rtItems.push({ to: '/agence-map', icon: Sun, label: 'Météo des Agences' });
  }
  if (user?.role === 'admin' && canAccess('organigramme')) {
    rtItems.push({ to: '/organigramme', icon: Network, label: 'Organigramme' });
  }
  if (user?.role === 'admin' && orgAccess.can_manage) {
    rtItems.push({ to: '/organisation', icon: Building2, label: 'Organisation' });
  }
  // ── Mes Objectifs : au-dessus de "Mes Documents" pour vendeurs/managers ──
  if ((user?.role === 'vendor' || user?.role === 'manager' || user?.role === 'responsable_agence') && canAccess('mes-objectifs')) {
    rtItems.push({ to: '/mes-objectifs', icon: Target, label: 'Mes Objectifs' });
  }
  if (canAccess('seller-space')) {
    rtItems.push({ to: '/mes-documents', icon: FolderOpen, label: 'Mes Documents' });
    rtItems.push({ to: '/seller-space', icon: User, label: 'Mon espace' });
  }
  if (canAccess('suivi-cartons')) {
    rtItems.push({ to: '/suivi-cartons', icon: AlertTriangle, label: 'Suivi RH' });
  }
  if (canAccess('assistant-ia') || user?.email?.toLowerCase() === 'bo.rtdistribution@gmail.com') {
    rtItems.push({ to: '/assistant-ia', icon: Bot, label: 'Assistant IA' });
  }
  // ── Objectifs Vendeurs (admin) ─────────────────────────────────────────────
  if (user?.role === 'admin' && canAccess('mes-objectifs')) {
    rtItems.push({ to: '/mes-objectifs', icon: Target, label: 'Objectifs Vendeurs' });
  }
  if (user?.role === 'admin') {
    rtItems.push({ to: '/admin', icon: Users, label: 'Administration' });
  }
  if (user?.role === 'admin' && user?.email === 'bo.rtdistribution@gmail.com') {
    rtItems.push({ to: '/db-admin', icon: Database, label: 'Bases de données' });
  }

  // ── Groupe ILEK (Phase 4) ───────────────────────────────────────────────────
  // RÈGLE #1 (prioritaire) : le scope partenaire (`allowed_partners`) gate les items.
  //   → Un user sans accès au partenaire 'ilek' ne voit JAMAIS ces items, quoi que dise le RBAC.
  // Ensuite seulement, le RBAC (canAccess) filtre selon le rôle.
  const ilekItems = [];
  const allowedPartnerSlugs = (user?.allowed_partners || []).map(p => p.slug);
  if (allowedPartnerSlugs.includes('ilek')) {
    if (canAccess('ilek-dashboard')) ilekItems.push({ to: '/ilek/dashboard', icon: LayoutDashboard, label: 'Dashboard' });
    if (canAccess('ilek-contracts')) ilekItems.push({ to: '/ilek/contracts', icon: FileText, label: 'Contrats' });
    if (canAccess('ilek-qualite'))   ilekItems.push({ to: '/ilek/qualite',   icon: Phone,          label: 'Qualité' });
  }

  // ── Groupe Bouygues ─────────────────────────────────────────────────────────
  const bouyguesItems = [];
  if (allowedPartnerSlugs.includes('bouygues')) {
    if (canAccess('bouygues-dashboard')) bouyguesItems.push({ to: '/bouygues/dashboard', icon: LayoutDashboard, label: 'Dashboard' });
    if (canAccess('bouygues-contracts')) bouyguesItems.push({ to: '/bouygues/contracts', icon: FileText, label: 'Contrats' });
  }

  // ── Groupe Octopus ne s'affiche que si user a accès au partenaire 'octopus' ──
  // (`hasOctopus` est déjà défini plus haut pour gater le contenu des items)

  const navGroups = [
    ...(hasOctopus && octopusItems.length > 0 ? [{ id: 'octopus', label: 'Octopus Energy', items: octopusItems }] : []),
    ...(ilekItems.length > 0 ? [{ id: 'ilek', label: 'ILEK', items: ilekItems }] : []),
    ...(bouyguesItems.length > 0 ? [{ id: 'bouygues', label: 'Bouygues Telecom', items: bouyguesItems }] : []),
    ...(rtItems.length > 0 ? [{ id: 'rt', label: 'RT Distribution', items: rtItems }] : []),
  ];

  // ── Ordre personnalisé du menu (admin maître uniquement, appliqué à tous) ───
  const isNavOrderAdmin = (user?.email || '').toLowerCase() === 'bo.rtdistribution@gmail.com';
  const [navOrderMap, setNavOrderMap] = useState({});
  const [navEditMode, setNavEditMode] = useState(false);
  const [draftItemsByGroup, setDraftItemsByGroup] = useState({});
  const [savingNavOrder, setSavingNavOrder] = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/nav-order`).then(({ data }) => setNavOrderMap(data.groups || {})).catch(() => {});
  }, []);

  // Applique l'ordre sauvegardé (items non répertoriés ajoutés à la fin, dans leur ordre d'origine)
  const orderedNavGroups = navGroups.map(g => {
    const savedOrder = navOrderMap[g.id];
    if (!savedOrder || !savedOrder.length) return g;
    // NB: `Map` est shadowé par l'icône lucide-react importée en haut du fichier
    // → on utilise un objet simple pour l'index des items restants.
    const remaining = {};
    g.items.forEach(it => { remaining[it.to] = it; });
    const ordered = [];
    savedOrder.forEach(to => {
      if (remaining[to]) { ordered.push(remaining[to]); delete remaining[to]; }
    });
    g.items.forEach(it => { if (remaining[it.to]) ordered.push(it); });
    return { ...g, items: ordered };
  });

  const startNavEditMode = () => {
    const draft = {};
    orderedNavGroups.forEach(g => { draft[g.id] = g.items; });
    setDraftItemsByGroup(draft);
    setNavEditMode(true);
  };

  const handleSaveNavOrder = async () => {
    setSavingNavOrder(true);
    try {
      // Ne persister que les groupes réellement modifiés (éviter d'écraser
      // silencieusement l'ordre global des groupes non touchés par ce drag).
      const entries = Object.entries(draftItemsByGroup).filter(([groupId, items]) => {
        const original = orderedNavGroups.find(g => g.id === groupId)?.items || [];
        return items.length !== original.length || items.some((it, i) => it.to !== original[i]?.to);
      });
      await Promise.all(entries.map(([groupId, items]) =>
        axios.put(`${API_URL}/nav-order`, { group_id: groupId, order: items.map(it => it.to) })
      ));
      setNavOrderMap(prev => {
        const next = { ...prev };
        entries.forEach(([groupId, items]) => { next[groupId] = items.map(it => it.to); });
        return next;
      });
      setNavEditMode(false);
      setDraftItemsByGroup({});
      toast.success('Ordre du menu enregistré pour tous les utilisateurs');
    } catch {
      toast.error("Erreur lors de l'enregistrement de l'ordre du menu");
    } finally {
      setSavingNavOrder(false);
    }
  };

  const cancelNavEditMode = () => {
    setNavEditMode(false);
    setDraftItemsByGroup({});
  };

  // Sidebar content component (reusable for desktop and mobile)
  const SidebarContent = ({ onNavigate }) => (
    <div className='flex flex-col h-full'>
      <nav className='flex-1 p-2 space-y-1 overflow-y-auto min-h-0' data-testid='sidebar-nav'>
        {isNavOrderAdmin && !isSidebarCollapsed && (
          <div className='px-1 pb-2'>
            {!navEditMode ? (
              <button
                onClick={startNavEditMode}
                data-testid='nav-reorder-toggle-btn'
                className='w-full flex items-center gap-1.5 px-2 py-1.5 rounded-md text-[11px] font-medium text-muted-foreground border border-dashed border-muted-foreground/30 hover:text-foreground hover:border-primary/40 hover:bg-secondary/40 transition-colors'
              >
                <ArrowUpDown className='size-3' /> Réorganiser le menu
              </button>
            ) : (
              <div className='flex items-center gap-2'>
                <button
                  onClick={handleSaveNavOrder}
                  disabled={savingNavOrder}
                  data-testid='nav-reorder-save-btn'
                  className='flex-1 flex items-center justify-center gap-1.5 text-xs font-medium bg-primary text-primary-foreground rounded-md py-1.5 hover:opacity-90 transition-opacity disabled:opacity-50'
                >
                  {savingNavOrder ? <Loader2 className='size-3 animate-spin' /> : <Check className='size-3' />} Enregistrer
                </button>
                <button
                  onClick={cancelNavEditMode}
                  disabled={savingNavOrder}
                  data-testid='nav-reorder-cancel-btn'
                  className='px-3 text-xs text-muted-foreground hover:text-foreground transition-colors'
                >
                  Annuler
                </button>
              </div>
            )}
          </div>
        )}
        {orderedNavGroups.map((group, gi) => {
          const isGroupCollapsed = navEditMode ? false : !!collapsedGroups[group.id];
          const groupItems = navEditMode ? (draftItemsByGroup[group.id] || group.items) : group.items;
          return (
            <div key={group.id} className={gi > 0 ? 'mt-2' : ''}>
              {/* En-tête de groupe — visible uniquement si sidebar développée */}
              {!isSidebarCollapsed ? (
                <button
                  className={`w-full flex items-center justify-between px-2 py-1 rounded-md text-[11px] font-bold uppercase tracking-widest text-muted-foreground transition-colors mb-0.5 ${navEditMode ? 'cursor-default' : 'hover:text-foreground hover:bg-secondary/40'}`}
                  onClick={() => !navEditMode && toggleGroup(group.id)}
                  data-testid={`group-toggle-${group.id}`}
                >
                  <span>{group.label}</span>
                  {isGroupCollapsed
                    ? <ChevronRightSmall className='size-3' />
                    : <ChevronDown className='size-3' />
                  }
                </button>
              ) : (
                gi > 0 && <div className='h-px bg-border mx-2 my-2' />
              )}

              {/* Items du groupe */}
              {!isGroupCollapsed && (navEditMode ? (
                <Reorder.Group
                  axis='y'
                  values={groupItems}
                  onReorder={(newItems) => setDraftItemsByGroup(prev => ({ ...prev, [group.id]: newItems }))}
                  className='space-y-0.5'
                  data-testid={`nav-reorder-group-${group.id}`}
                >
                  {groupItems.map((item) => (
                    <Reorder.Item
                      key={item.to}
                      value={item}
                      data-testid={`nav-reorder-item-${item.to}`}
                      className='flex items-center gap-3 px-3 py-2 rounded-md text-sm bg-secondary/40 border border-dashed border-muted-foreground/30 cursor-grab active:cursor-grabbing select-none'
                    >
                      <GripVertical className='size-4 text-muted-foreground shrink-0' />
                      <item.icon className='size-5 shrink-0' />
                      <span>{item.label}</span>
                    </Reorder.Item>
                  ))}
                </Reorder.Group>
              ) : groupItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  onMouseEnter={() => prefetchRoute(item.to)}
                  onFocus={() => prefetchRoute(item.to)}
                  onClick={(e) => {
                    if (item.to === '/contracts' && quizGate.locked) {
                      e.preventDefault();
                      quizGate.openPopup();
                      return;
                    }
                    onNavigate?.();
                    if (item.to === '/chat') setChatUnreadCount(0);
                  }}
                  className={({ isActive }) => {
                    const isHR = item.to === '/suivi-cartons';
                    if (item.to === '/contracts' && quizGate.locked) {
                      return `flex items-center gap-3 px-3 py-2 rounded-md text-sm opacity-50 cursor-not-allowed text-muted-foreground ${isSidebarCollapsed ? 'justify-center' : ''}`;
                    }
                    return `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                      isActive
                        ? (isHR ? 'bg-amber-500 text-white shadow-sm shadow-amber-500/40' : 'bg-primary text-primary-foreground')
                        : (isHR
                          ? 'bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700/60 text-amber-800 dark:text-amber-300 font-semibold hover:bg-amber-100 dark:hover:bg-amber-900/30'
                          : 'hover:bg-secondary text-foreground')
                    } ${isSidebarCollapsed ? 'justify-center' : ''}`;
                  }}
                  data-testid={`nav-${item.label.toLowerCase()}`}
                  title={isSidebarCollapsed ? item.label : ''}
                >
                  <div className='relative'>
                    <item.icon className='size-5' />
                    {item.to === '/chat' && chatUnreadCount > 0 && (
                      <span className='absolute -top-1.5 -right-1.5 h-4 w-4 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center'>
                        {chatUnreadCount > 9 ? '9+' : chatUnreadCount}
                      </span>
                    )}
                  </div>
                  {!isSidebarCollapsed && (
                    <span className='flex items-center gap-2'>
                      {item.label}
                      {item.to === '/contracts' && quizGate.locked && <Lock className='size-3.5' data-testid='nav-contracts-lock' />}
                      {item.to === '/chat' && chatUnreadCount > 0 && (
                        <span className='h-5 min-w-[20px] px-1.5 rounded-full bg-red-500 text-white text-xs font-bold flex items-center justify-center'>
                          {chatUnreadCount > 99 ? '99+' : chatUnreadCount}
                        </span>
                      )}
                    </span>
                  )}
                </NavLink>
              )))}
            </div>
          );
        })}
      </nav>

      <div className='p-3 border-t space-y-1 shrink-0 bg-background'>
        {!isSidebarCollapsed ? (
          <>
            {/* Bandeau impersonnation actif */}
            {isImpersonating && (
              <div className='mb-2 px-2 py-1.5 rounded-lg bg-violet-50 dark:bg-violet-900/20 border border-violet-200 dark:border-violet-700 flex items-center gap-2'>
                <Eye className='size-3.5 text-violet-500 shrink-0' />
                <div className='flex-1 min-w-0'>
                  <p className='text-xs font-semibold text-violet-700 dark:text-violet-300 truncate'>Vue simulée</p>
                  <p className='text-[10px] text-violet-500 truncate'>{impersonation?.user?.vendor_name || impersonation?.user?.email}</p>
                </div>
                <Button
                  size='icon'
                  variant='ghost'
                  className='h-6 w-6 text-violet-500 hover:text-violet-700 hover:bg-violet-100 dark:hover:bg-violet-900/40 shrink-0'
                  onClick={stopImpersonation}
                  title='Quitter la vue simulée'
                  data-testid='stop-impersonation-btn'
                >
                  <EyeOff className='size-3.5' />
                </Button>
              </div>
            )}
            <div className='flex items-center gap-3 px-3 py-2'>
              <Avatar className={`size-8 ${isImpersonating ? 'ring-2 ring-violet-400 ring-offset-1' : ''}`}>
                <AvatarFallback className={`text-xs ${isImpersonating ? 'bg-violet-100 text-violet-700' : 'bg-primary text-primary-foreground'}`}>
                  {user?.email?.charAt(0).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div className='flex-1 min-w-0'>
                <p className='text-sm font-medium truncate'>{user?.email}</p>
                <p className='text-xs text-muted-foreground capitalize'>{user?.role}</p>
              </div>
              <div className='flex items-center gap-1'>
                {/* Bouton simuler vue — admin uniquement et hors impersonnation */}
                {!isImpersonating && user?.role === 'admin' && (
                  <Button
                    variant='ghost'
                    size='icon'
                    className='h-6 w-6 text-muted-foreground hover:text-violet-600 hover:bg-violet-50 dark:hover:bg-violet-900/20'
                    onClick={() => setShowImpersonationModal(true)}
                    title='Simuler une vue utilisateur'
                    data-testid='open-impersonation-btn'
                  >
                    <Eye className='size-3.5' />
                  </Button>
                )}
                {/* Toggle thème */}
                <div className='flex items-center bg-secondary/50 rounded-full p-1 border'>
                  <Button
                    variant='ghost'
                    size='icon'
                    className={`h-6 w-6 rounded-full ${theme === 'light' ? 'bg-background shadow-sm text-yellow-500' : 'text-muted-foreground hover:text-foreground'}`}
                    onClick={() => theme !== 'light' && toggleTheme()}
                    title='Mode clair'
                  >
                    <Sun className='size-3.5' />
                  </Button>
                  <Button
                    variant='ghost'
                    size='icon'
                    className={`h-6 w-6 rounded-full ${theme === 'dark' ? 'bg-background shadow-sm text-blue-400' : 'text-muted-foreground hover:text-foreground'}`}
                    onClick={() => theme !== 'dark' && toggleTheme()}
                    title='Mode sombre'
                  >
                    <Moon className='size-3.5' />
                  </Button>
                </div>
              </div>
            </div>
            <Button
              variant='ghost'
              className='w-full justify-start text-muted-foreground hover:text-foreground'
              onClick={() => { setPasswordError(''); setPasswordSuccess(false); setShowPasswordModal(true); }}
              data-testid='change-password-btn'
            >
              <KeyRound className='size-4 mr-2' />
              Changer le mot de passe
            </Button>
            <Button
              variant='ghost'
              className='w-full justify-start'
              onClick={() => {
                handleLogout();
                onNavigate?.();
              }}
              data-testid='logout-button'
            >
              <LogOut className='size-4 mr-2' />
              Déconnexion
            </Button>
          </>
        ) : (
          <>
            <Avatar className={`size-8 mx-auto mb-2 ${isImpersonating ? 'ring-2 ring-violet-400 ring-offset-1' : ''}`}>
              <AvatarFallback className={`text-xs ${isImpersonating ? 'bg-violet-100 text-violet-700' : 'bg-primary text-primary-foreground'}`}>
                {user?.email?.charAt(0).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            
            <div className='flex items-center justify-center bg-secondary/50 rounded-full p-1 border mb-2 mx-auto w-fit'>
              <Button
                variant='ghost'
                size='icon'
                className={`h-6 w-6 rounded-full ${theme === 'light' ? 'bg-background shadow-sm text-yellow-500' : 'text-muted-foreground hover:text-foreground'}`}
                onClick={() => theme !== 'light' && toggleTheme()}
                title='Mode clair'
              >
                <Sun className='size-3.5' />
              </Button>
              <Button
                variant='ghost'
                size='icon'
                className={`h-6 w-6 rounded-full ${theme === 'dark' ? 'bg-background shadow-sm text-blue-400' : 'text-muted-foreground hover:text-foreground'}`}
                onClick={() => theme !== 'dark' && toggleTheme()}
                title='Mode sombre'
              >
                <Moon className='size-3.5' />
              </Button>
            </div>

            {/* Simuler vue (sidebar réduite) */}
            {!isImpersonating && user?.role === 'admin' && (
              <Button
                variant='ghost'
                size='sm'
                className='w-full justify-center text-muted-foreground hover:text-violet-600 hover:bg-violet-50 dark:hover:bg-violet-900/20 mb-1'
                onClick={() => setShowImpersonationModal(true)}
                title='Simuler une vue utilisateur'
                data-testid='open-impersonation-btn-collapsed'
              >
                <Eye className='size-4' />
              </Button>
            )}
            {/* Quitter impersonnation (sidebar réduite) */}
            {isImpersonating && (
              <Button
                variant='ghost'
                size='sm'
                className='w-full justify-center text-violet-500 hover:text-violet-700 hover:bg-violet-50 dark:hover:bg-violet-900/20 mb-1'
                onClick={stopImpersonation}
                title='Quitter la vue simulée'
                data-testid='stop-impersonation-btn-collapsed'
              >
                <EyeOff className='size-4' />
              </Button>
            )}

            <Button
              variant='ghost'
              size='sm'
              className='w-full justify-center text-muted-foreground hover:text-foreground mb-1'
              onClick={() => { setPasswordError(''); setPasswordSuccess(false); setShowPasswordModal(true); }}
              title='Changer le mot de passe'
              data-testid='change-password-btn-collapsed'
            >
              <KeyRound className='size-4' />
            </Button>

            <Button
              variant='ghost'
              size='sm'
              className='w-full justify-center'
              onClick={() => {
                handleLogout();
                onNavigate?.();
              }}
              data-testid='logout-button'
              title='Déconnexion'
            >
              <LogOut className='size-4' />
            </Button>
          </>
        )}
      </div>
    </div>
  );

  return (
    <div className='flex h-screen bg-background overflow-hidden'>
      {/* Reminder Notifications Modal */}
      <ReminderNotifications />
      
      {/* Objectives Reminder Modal */}
      <ObjectivesReminder />
      <ClarificationAlert />
      
      {/* Desktop Sidebar - Hidden on mobile */}
      <aside 
        className={`${isSidebarCollapsed ? 'w-16' : 'w-64'} border-r bg-card flex-col transition-all duration-300 hidden lg:flex h-screen`} 
        data-testid='sidebar'
      >
        <div className='p-4 border-b flex items-center justify-between shrink-0'>
          {!isSidebarCollapsed && (
            <div>
              <h1 className='text-xl font-semibold text-primary'>RT Distribution</h1>
              <p className='text-xs text-muted-foreground mt-1'>CRM Ventes</p>
            </div>
          )}
          <div className='flex items-center gap-1'>
            {/* Cloche visible uniquement sidebar ouverte */}
            {!isSidebarCollapsed && (
              <NotificationBell onArticleClick={handleArticleClick} key={notificationKey} />
            )}
            <Button
              variant='ghost'
              size='sm'
              onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
              className={isSidebarCollapsed ? 'w-full' : ''}
              data-testid='toggle-sidebar-button'
            >
              {isSidebarCollapsed ? <ChevronRight className='size-4' /> : <ChevronLeft className='size-4' />}
            </Button>
          </div>
        </div>

        <div className='flex-1 min-h-0 overflow-hidden'>
          <SidebarContent onNavigate={() => {}} />
        </div>
      </aside>

      {/* Mobile Header - Visible only on mobile */}
      <div id='mobile-nav-header' className='lg:hidden fixed top-0 left-0 right-0 z-40 bg-card border-b' style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}>
        <div className='flex items-center justify-between p-4'>
          <div>
            <h1 className='text-lg font-semibold text-primary'>RT Distribution</h1>
            <p className='text-xs text-muted-foreground'>CRM Ventes</p>
          </div>
          <div className='flex items-center gap-2'>
            {/* Refresh Button */}
            <button
              onClick={handleRefresh}
              className="relative p-2 text-gray-600 hover:text-gray-800 focus:outline-none transition-colors"
              aria-label="Actualiser"
            >
              <RotateCw className="w-6 h-6" />
            </button>
            {/* Notification Bell */}
            <NotificationBell 
              key={notificationKey}
              onArticleClick={handleArticleClick} 
            />
            {/* Chat Notification - visible si accès chat (rôle ou membership) */}
            {((chatEnabled || canManageChat) || userHasChatAccess) && (
              <NavLink
                to='/chat'
                onMouseEnter={() => prefetchRoute('/chat')}
                onFocus={() => prefetchRoute('/chat')}
                className='relative p-2 text-gray-600 hover:text-gray-800 focus:outline-none transition-colors'
                aria-label='Messages'
              >
                <MessageSquare className='w-6 h-6' />
                {chatUnreadCount > 0 && (
                  <span className='absolute -top-0.5 -right-0.5 h-5 min-w-[20px] px-1 rounded-full bg-red-500 text-white text-[11px] font-bold flex items-center justify-center'>
                    {chatUnreadCount > 99 ? '99+' : chatUnreadCount}
                  </span>
                )}
              </NavLink>
            )}
            {/* Menu Hamburger */}
            <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
              <SheetTrigger asChild>
                <Button variant='ghost' size='sm'>
                  <Menu className='size-6' />
                </Button>
              </SheetTrigger>
              <SheetContent side='left' className='w-64 p-0 flex flex-col'>
                <SheetTitle className='sr-only'>Menu de navigation</SheetTitle>
                <div className='p-4 border-b flex items-center justify-between shrink-0'>
                  <div>
                    <h1 className='text-xl font-semibold text-primary'>RT Distribution</h1>
                    <p className='text-xs text-muted-foreground mt-1'>CRM Ventes</p>
                  </div>
                </div>
                <div className='flex-1 overflow-y-auto min-h-0'>
                  <SidebarContent onNavigate={() => setIsMobileMenuOpen(false)} />
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>

      {/* Main content with padding for mobile header */}
      <main className='flex-1 overflow-y-auto overflow-x-hidden lg:pt-0 pt-16'>
        <Outlet />
      </main>

      <NotePopupGate />
      {quizGate.popup}

      {/* Notification de nouveaux articles */}
      <NewArticleNotification onArticleClick={handleArticleClick} />

      {/* Article Modal */}
      {showArticleModal && selectedArticle && (
        <ArticleModal
          article={selectedArticle}
          onClose={handleArticleClose}
          onRead={handleArticleRead}
          onRemindLater={handleArticleRemindLater}
        />
      )}

      {/* Modal de sélection de profil à simuler (admin uniquement) */}
      <ImpersonationSelector
        open={showImpersonationModal}
        onOpenChange={setShowImpersonationModal}
      />

      {/* Modal — Changer le mot de passe */}
      {showPasswordModal && (
        <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm'>
          <div className='bg-card border rounded-2xl shadow-2xl w-full max-w-md mx-4 p-6'>
            <div className='flex items-center gap-3 mb-5'>
              <div className='p-2 rounded-full bg-primary/10'>
                <KeyRound className='size-5 text-primary' />
              </div>
              <div>
                <h2 className='text-lg font-semibold'>Changer le mot de passe</h2>
                <p className='text-xs text-muted-foreground'>{user?.email}</p>
              </div>
              <button
                onClick={() => setShowPasswordModal(false)}
                className='ml-auto p-1.5 rounded-full hover:bg-muted transition-colors text-muted-foreground hover:text-foreground'
                data-testid='close-password-modal'
              >
                <X className='size-4' />
              </button>
            </div>

            {passwordSuccess ? (
              <div className='flex flex-col items-center gap-3 py-6 text-center'>
                <div className='p-3 rounded-full bg-green-100 dark:bg-green-900/30'>
                  <KeyRound className='size-6 text-green-600 dark:text-green-400' />
                </div>
                <p className='text-green-700 dark:text-green-400 font-semibold'>Mot de passe mis à jour !</p>
              </div>
            ) : (
              <form onSubmit={handleChangePassword} className='space-y-4'>
                <div>
                  <label className='text-sm font-medium block mb-1'>Mot de passe actuel</label>
                  <input
                    type='password'
                    required
                    value={passwordForm.old_password}
                    onChange={e => setPasswordForm(p => ({ ...p, old_password: e.target.value }))}
                    className='w-full border rounded-lg px-3 py-2 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-primary'
                    placeholder='••••••••'
                    data-testid='old-password-input'
                  />
                </div>
                <div>
                  <label className='text-sm font-medium block mb-1'>Nouveau mot de passe</label>
                  <input
                    type='password'
                    required
                    value={passwordForm.new_password}
                    onChange={e => setPasswordForm(p => ({ ...p, new_password: e.target.value }))}
                    className='w-full border rounded-lg px-3 py-2 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-primary'
                    placeholder='••••••••'
                    data-testid='new-password-input'
                  />
                </div>
                <div>
                  <label className='text-sm font-medium block mb-1'>Confirmer le nouveau mot de passe</label>
                  <input
                    type='password'
                    required
                    value={passwordForm.confirm}
                    onChange={e => setPasswordForm(p => ({ ...p, confirm: e.target.value }))}
                    className='w-full border rounded-lg px-3 py-2 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-primary'
                    placeholder='••••••••'
                    data-testid='confirm-password-input'
                  />
                </div>

                {passwordError && (
                  <p className='text-sm text-red-500 bg-red-50 dark:bg-red-900/20 px-3 py-2 rounded-lg' data-testid='password-error'>
                    {passwordError}
                  </p>
                )}

                <div className='flex gap-3 pt-1'>
                  <Button
                    type='button'
                    variant='outline'
                    className='flex-1'
                    onClick={() => setShowPasswordModal(false)}
                  >
                    Annuler
                  </Button>
                  <Button
                    type='submit'
                    className='flex-1'
                    disabled={passwordLoading}
                    data-testid='submit-password-btn'
                  >
                    {passwordLoading ? 'Enregistrement...' : 'Enregistrer'}
                  </Button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}